const DRIVER_NAME_COLLATOR = new Intl.Collator("fr-CA", {
  numeric: true,
  sensitivity: "base",
});

function normalizeOptionalDriverText(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function normalizeDriverNameKey(value) {
  return (normalizeOptionalDriverText(value) || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr-CA")
    .replace(/\s+/g, " ");
}

export function buildDeliveryDriverOptions(trucks = []) {
  const candidatesByName = new Map();

  (Array.isArray(trucks) ? trucks : []).forEach((truck) => {
    [
      {
        name: truck?.driver_name,
        userId: truck?.driver_user_id,
      },
      {
        name: truck?.evening_driver_name,
        userId: truck?.evening_driver_user_id,
      },
    ].forEach((candidate) => {
      const name = normalizeOptionalDriverText(candidate.name);
      const normalizedName = normalizeDriverNameKey(name);
      if (!name || !normalizedName) {
        return;
      }

      const currentCandidate = candidatesByName.get(normalizedName) || {
        hasUnlinkedSource: false,
        name,
        userIds: new Set(),
      };
      const userId = normalizeOptionalDriverText(candidate.userId);
      if (userId) {
        currentCandidate.userIds.add(userId);
      } else {
        currentCandidate.hasUnlinkedSource = true;
      }
      candidatesByName.set(normalizedName, currentCandidate);
    });
  });

  return Array.from(candidatesByName.values())
    .map((candidate) => ({
      name: candidate.name,
      userId: candidate.userIds.size === 1 && !candidate.hasUnlinkedSource
        ? Array.from(candidate.userIds)[0]
        : null,
    }))
    .sort((leftOption, rightOption) => DRIVER_NAME_COLLATOR.compare(leftOption.name, rightOption.name));
}

export function resolveDeliveryDriverUserId(driverName, driverOptions = []) {
  const normalizedName = normalizeDriverNameKey(driverName);
  if (!normalizedName) {
    return null;
  }

  const matchingOptions = (Array.isArray(driverOptions) ? driverOptions : [])
    .filter((option) => normalizeDriverNameKey(option?.name) === normalizedName);
  if (matchingOptions.length !== 1) {
    return null;
  }

  return normalizeOptionalDriverText(matchingOptions[0]?.userId);
}

export function canUpdateDeliveryDriverAttribution({
  deliveryIsDelivered = false,
  markDelivered = false,
} = {}) {
  return deliveryIsDelivered === true || markDelivered === true;
}

export function resolveOrderDeliveredByDriverSummary(order) {
  const deliveries = Array.isArray(order?.deliveries) ? order.deliveries.filter(Boolean) : [];
  const uniqueNames = [];
  const seenNames = new Set();

  deliveries.forEach((delivery) => {
    const name = normalizeOptionalDriverText(delivery?.delivered_by_driver_name);
    const normalizedName = normalizeDriverNameKey(name);
    if (!name || !normalizedName || seenNames.has(normalizedName)) {
      return;
    }
    seenNames.add(normalizedName);
    uniqueNames.push(name);
  });

  if (uniqueNames.length === 0) {
    return { display: "—", names: [], title: undefined };
  }

  const titleLines = deliveries.map((delivery, index) => {
    const deliveryLabel = normalizeOptionalDriverText(delivery?.client_name)
      || normalizeOptionalDriverText(delivery?.delivery_id)
      || `Livraison ${index + 1}`;
    const driverName = normalizeOptionalDriverText(delivery?.delivered_by_driver_name) || "Non renseigné";
    return `${deliveryLabel} · ${driverName}`;
  });

  return {
    display: uniqueNames.length === 1 ? uniqueNames[0] : `${uniqueNames[0]} +${uniqueNames.length - 1}`,
    names: uniqueNames,
    title: titleLines.join("\n") || uniqueNames.join("\n"),
  };
}
